import { ALL_ITEMS_PAGE_SIZE, defaultPagination } from 'core/http/pagination';
import { groupBy } from 'lodash';
import { useCallback } from 'react';
import { Declaration, NotifyEmails } from 'store/declarations/declaration';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import {
    ICS2ConsignmentHouseLevel,
    ICS2EnsDeclarationPayload,
    ICS2EnsDeclarationRequest,
    ICS2GoodsItem,
    ICS2GoodsItemCategory,
    toIcs2EnsDeclarationRequest,
    toIcs2EnsPayloadGoodsItem,
} from 'store/declarations/eu/ics2/ics2-ens-declaration';
import { listIcs2EnsProducts } from 'store/products/client';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import {
    ElectronicTransportDocument,
    ElectronicTransportDocumentPayload,
} from 'store/declarations/ireland/electronic-transport-document';
import {
    IrelandTemporaryStorageDeclaration,
    IrelandTemporaryStorageDeclarationPayload,
} from 'store/declarations/ireland/temporary-storage-declaration';
import {
    IrelandEntrySummaryDeclaration,
    IrelandEntrySummaryDeclarationPayload,
} from 'store/declarations/ireland/entry-summary-declaration';
import {
    IrelandExportDeclaration,
    IrelandExportDeclarationPayload,
} from 'store/declarations/ireland/export-declaration';
import {
    IrelandH7ImportDeclaration,
    IrelandH7ImportDeclarationPayload,
} from 'store/declarations/ireland/h7-import-declaration';
import {
    IrelandImportDeclaration,
    IrelandImportDeclarationPayload,
} from 'store/declarations/ireland/import-declaration';
import { CdsDeclaration, CdsDeclarationPayload } from 'store/declarations/uk/cds-declaration';
import { useAppDispatch, useAppSelector } from '../config/hooks';
import {
    doCreateAmendmentIrelandEntrySummaryDeclaration,
    doCreateAmendmentIrelandExportDeclaration,
    doCreateAmendmentIrelandImportDeclaration,
    doCreateArrivalAtExitDeclaration,
    doCreateElectronicTransportDocument,
    doCreateEntrySummaryDeclaration,
    doCreateIcs2EnsDeclaration,
    doUpdateIcs2EnsDeclaration,
    doAmendIcs2EnsDeclaration,
    doCreateTemporaryStorageDeclaration,
    doCreateIrelandExportDeclaration,
    doCreateIrelandH7ImportDeclaration,
    doCreateIrelandImportDeclaration,
    doCreateCdsDeclaration,
    doDuplicateDeclaration,
    doGetDeclaration,
    doGetDeclarationDocuments,
    doGetDeclarationHistory,
    doInvalidateDeclaration,
    doInvalidateExportDeclaration,
    doInvalidateNctsDeclaration,
    doInvalidateUkNctsDeclaration,
    doSubmitDeclaration,
    doSubmitIrelandImportDocuments,
    doSubmitImportPresentationNotification,
    doUpdateArrivalAtExitDeclaration,
    doToggleCoreTemplateStatus,
    doUpdateElectronicTransportDocument,
    doUpdateEntrySummaryDeclaration,
    doUpdateIrelandExportDeclaration,
    doUpdateIrelandH7ImportDeclaration,
    doUpdateIrelandImportDeclaration,
    doUpdateTemporaryStorageDeclaration,
    doCreateIrelandH1ImportDeclarationWithFile,
    doCreateIrelandH7ImportDeclarationsWithBulkUpload,
    doGetDeclarationTemplate,
    doCancelCdsDeclaration,
    doCancelAmendmentIrelandExportDeclaration,
    doCreateUkGvmsRecord,
    doUpdateUkGvmsRecord,
    doUpdateCdsDeclaration,
    doCancelAmendmentGmrRecord,
    doCreateAmendmentGmrRecord,
    doCreateIePbnRecord,
    doUpdateIePbnRecord,
    doCreateNctsDeclaration,
    doCreateUkNctsDeclaration,
    doUpdateNctsDeclaration,
    doUpdateUkNctsDeclaration,
} from '../store/declarations/actions';
import useResponseNotification from 'hooks/useResponseNotification';
import { useTranslation } from 'react-i18next';
import {
    cancelAmendmentNotifs,
    removeDeclarationAsCoreTemplateNotifs,
    setDeclarationAsCoreTemplateNotifs,
    submitDeclarationNotifs,
    updateDeclarationNotifs,
} from '../utils/notificationMessages';
import { UkGvmsRecord } from '../store/declarations/uk/gvms-declaration';
import { ArrivalAtExitDeclarationPayload } from '../store/declarations/ireland/arrival-at-exit-declaration';
import {
    AESMessageTypes,
    ICS2MessageTypes,
    toIcs2AmendmentMessageType,
} from '../store/declarations/enums/common/declaration-types';
import moment from 'moment';
import { formatDateTimeToISO8601UTCTimestamp } from 'views/declarations/eu/utils';
import { IrelandPbnRecord } from '../store/declarations/ireland/pbn-declaration';
import { NctsDeclaration } from '../store/declarations/ireland/ncts-declaration';
import { calculateGrossMass } from 'views/declarations/declaration-tabs/declaration-form/utils/grossMassUtils';

export type UpdateDeclarationFunction = (
    customerId: string,
    declarationId: string,
    declaration: any,
    withNotification?: boolean
) => Promise<any>;

const getEnsIcs2HouseConsignments = (declaration: Declaration): ICS2ConsignmentHouseLevel[] =>
    declaration.ics2ENSDeclaration?.payload?.consignmentMasterLevel?.consignmentHouseLevel ?? [];

/**
 * Writes the goods items the products endpoints hold into the request about to be sent. A
 * declaration update rebuilds the items of the declaration from the payload it carries - one with an
 * `id` is updated, one without is created and one that is missing is deleted - both on the master
 * level and within every house consignment. The items themselves are created, updated and deleted
 * through their own endpoints, so the copy loaded with the declaration goes stale as soon as one of
 * those runs, and sending it back would undo them: they are listed again here instead.
 */
const numbered = (goodsItems: ICS2GoodsItem[]): ICS2GoodsItem[] =>
    goodsItems.map((goodsItem, index) => ({ ...goodsItem, goodsItemNumber: index + 1 }));

const withFreshEnsIcs2GoodsItems = async (
    declarationId: string,
    request: ICS2EnsDeclarationRequest
): Promise<ICS2EnsDeclarationRequest> => {
    const listItems = async (category: ICS2GoodsItemCategory) => {
        const listed = await listIcs2EnsProducts(declarationId, category, { size: ALL_ITEMS_PAGE_SIZE });

        // Taking a failed listing for a level with no items would send a payload that deletes every
        // item on it.
        if (!listed?.list) throw new Error('Could not list the goods items of the declaration.');

        return listed.list;
    };

    const payload: ICS2EnsDeclarationPayload = request.payload ?? {};
    const houseItemsByConsignment = groupBy(await listItems(ICS2GoodsItemCategory.HOUSE), 'houseConsignmentId');

    const consignmentMasterLevel: NonNullable<ICS2EnsDeclarationPayload['consignmentMasterLevel']> = {
        ...(payload.consignmentMasterLevel ?? {}),
        consignmentHouseLevel: (payload.consignmentMasterLevel?.consignmentHouseLevel ?? []).map((consignment) => ({
            ...consignment,
            // A consignment without an id is being created, so it holds no items yet.
            goodsItem: consignment.id
                ? numbered((houseItemsByConsignment[consignment.id] ?? []).map(toIcs2EnsPayloadGoodsItem))
                : [],
        })),
    };

    // F50 keeps every item on a house consignment: it has no master item level, and its message
    // would not carry the field at all.
    if (request.messageType === ICS2MessageTypes.F11) {
        consignmentMasterLevel.goodsItem = numbered(
            (await listItems(ICS2GoodsItemCategory.MASTER)).map(toIcs2EnsPayloadGoodsItem)
        );
    }

    return { ...request, payload: { ...payload, consignmentMasterLevel } };
};

const useDeclarations = () => {
    const declaration = useAppSelector((state) => state.declarations.declaration);
    const declarationTemplate = useAppSelector((state) => state.declarations.declarationTemplate);
    const isLoading = useAppSelector((state) => state.declarations.isLoading);
    const error = useAppSelector((state) => state.declarations.error);
    const documents = useAppSelector((state) => state.declarations.documents);
    const declarationHistory = useAppSelector((state) => state.declarations.declarationHistory);
    const { withResponseNotifications } = useResponseNotification();
    const { t } = useTranslation(['common', 'customs_declarations']);

    const dispatch = useAppDispatch();

    const getDeclaration = useCallback(
        (declarationId: string) => {
            return dispatch(doGetDeclaration(declarationId));
        },
        [dispatch]
    );
    const getDeclarationTemplate = useCallback(
        (templateId?: string) => {
            return dispatch(doGetDeclarationTemplate(templateId ?? declaration?.templateId));
        },
        [declaration?.templateId, dispatch]
    );

    const createIrelandImportDeclaration = useCallback(
        (
            customerId: string,
            irelandImportDeclaration?: IrelandImportDeclaration,
            jobId?: string,
            templateId?: string
        ) =>
            dispatch(
                doCreateIrelandImportDeclaration(
                    customerId,
                    {
                        jobId,
                        declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                        declarationInternalType: DeclarationInternalType.IMPORT,
                        irelandImportDeclaration: irelandImportDeclaration,
                    },
                    templateId
                )
            ),
        [dispatch]
    );

    const createIrelandH1ImportDeclarationWithFile = useCallback(
        (file: File, jobId: string, customerId: string): Promise<IrelandImportDeclarationPayload> | undefined =>
            dispatch(doCreateIrelandH1ImportDeclarationWithFile(file, jobId, customerId)),
        [dispatch]
    );

    const createIrelandH7ImportDeclaration = useCallback(
        (
            customerId: string,
            irelandH7ImportDeclaration?: IrelandH7ImportDeclaration,
            jobId?: string,
            templateId?: string
        ) =>
            dispatch(
                doCreateIrelandH7ImportDeclaration(
                    customerId,
                    {
                        jobId,
                        declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                        declarationInternalType: DeclarationInternalType.IMPORT,
                        irelandH7ImportDeclaration: irelandH7ImportDeclaration,
                    },
                    templateId
                )
            ),
        [dispatch]
    );

    const createIrelandH7ImportDeclarationsWithBulkUpload = useCallback(
        (s3Link: string, jobId: string, customerId: string): Promise<IrelandImportDeclarationPayload> | undefined =>
            dispatch(doCreateIrelandH7ImportDeclarationsWithBulkUpload(s3Link, jobId, customerId)),
        [dispatch]
    );

    const createIrelandExportDeclaration = useCallback(
        (customerId: string, ieExportDeclaration?: IrelandExportDeclaration, jobId?: string, templateId?: string) =>
            dispatch(
                doCreateIrelandExportDeclaration(
                    customerId,
                    {
                        jobId,
                        declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                        declarationInternalType: DeclarationInternalType.EXPORT,
                        ieExportDeclaration,
                    },
                    templateId
                )
            ),
        [dispatch]
    );

    const createEntrySummaryDeclaration = useCallback(
        (customerId: string, declaration?: IrelandEntrySummaryDeclaration, jobId?: string, templateId?: string) =>
            dispatch(
                doCreateEntrySummaryDeclaration(
                    customerId,
                    {
                        jobId,
                        declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                        declarationInternalType: DeclarationInternalType.ENS,
                        entrySummaryDeclaration: declaration,
                    },
                    templateId
                )
            ),
        [dispatch]
    );

    const createTemporaryStorageDeclaration = useCallback(
        (
            customerId: string,
            ieImportTemporaryStorageDeclaration?: IrelandTemporaryStorageDeclaration,
            jobId?: string
        ) =>
            dispatch(
                doCreateTemporaryStorageDeclaration(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                    declarationInternalType: DeclarationInternalType.TEMPORARY,
                    ieImportTemporaryStorageDeclaration,
                })
            ),
        [dispatch]
    );

    const createNctsDeclaration = useCallback(
        (customerId: string, ieNctsDeclaration?: NctsDeclaration, jobId?: string) =>
            dispatch(
                doCreateNctsDeclaration(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                    declarationInternalType: DeclarationInternalType.NCTS,
                    ieNctsDeclaration,
                })
            ),
        [dispatch]
    );

    const createUkNctsDeclaration = useCallback(
        (customerId: string, ukNctsDeclaration?: NctsDeclaration, jobId?: string) =>
            dispatch(
                doCreateUkNctsDeclaration(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.HMRC,
                    declarationInternalType: DeclarationInternalType.NCTS,
                    ukNctsDeclaration,
                })
            ),
        [dispatch]
    );

    const updateTemporaryStorageDeclaration = useCallback(
        async (customerId: string, declarationId: string, declaration: IrelandTemporaryStorageDeclarationPayload) =>
            withResponseNotifications(
                await dispatch(doUpdateTemporaryStorageDeclaration(customerId, declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const updateNctsDeclaration = useCallback(
        async (customerId: string, declarationId: string, declaration: NctsDeclaration) =>
            withResponseNotifications(
                await dispatch(doUpdateNctsDeclaration(customerId, declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const updateUkNctsDeclaration = useCallback(
        async (customerId: string, declarationId: string, declaration: NctsDeclaration) =>
            withResponseNotifications(
                await dispatch(doUpdateUkNctsDeclaration(customerId, declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const createUkImportNewDeclaration = useCallback(
        (customerId: string, cdsDeclaration?: CdsDeclaration, jobId?: string, templateId?: string) =>
            dispatch(
                doCreateCdsDeclaration(
                    customerId,
                    {
                        jobId,
                        declarationExternalEntity: DeclarationExternalEntity.CDS,
                        declarationInternalType: DeclarationInternalType.IMPORT_NEW,
                        cdsDeclaration,
                    },
                    templateId
                )
            ),
        [dispatch]
    );

    const createUkExportDeclaration = useCallback(
        (customerId: string, cdsDeclaration?: CdsDeclaration, jobId?: string, templateId?: string) =>
            dispatch(
                doCreateCdsDeclaration(
                    customerId,
                    {
                        jobId,
                        declarationExternalEntity: DeclarationExternalEntity.CDS,
                        declarationInternalType: DeclarationInternalType.EXPORT,
                        cdsDeclaration,
                    },
                    templateId
                )
            ),
        [dispatch]
    );

    const createGvmsRecord = useCallback(
        (customerId: string, gvmsDeclaration?: UkGvmsRecord, jobId?: string) =>
            dispatch(
                doCreateUkGvmsRecord(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.GVMS,
                    declarationInternalType: DeclarationInternalType.GVMS,
                    gvmsDeclaration,
                })
            ),
        [dispatch]
    );

    const createPbnRecord = useCallback(
        (customerId: string, preBoardingNotification?: IrelandPbnRecord, jobId?: string) =>
            dispatch(
                doCreateIePbnRecord(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                    declarationInternalType: DeclarationInternalType.PBN,
                    preBoardingNotification,
                })
            ),
        [dispatch]
    );

    const updateIrelandImportDeclaration = useCallback(
        async (
            customerId: string,
            declarationId: string,
            declaration: IrelandImportDeclarationPayload,
            withNotification: boolean = true
        ) => {
            if (withNotification) {
                return withResponseNotifications(
                    await dispatch(doUpdateIrelandImportDeclaration(customerId, declarationId, declaration)),
                    updateDeclarationNotifs(t)
                );
            } else {
                return await dispatch(
                    doUpdateIrelandImportDeclaration(customerId, declarationId, declaration, calculateGrossMass)
                );
            }
        },

        [dispatch, withResponseNotifications, t]
    );

    const updateIrelandH7ImportDeclaration = useCallback(
        async (
            customerId: string,
            declarationId: string,
            declaration: IrelandH7ImportDeclarationPayload,
            _: boolean = true,
            calculateGrossMass: boolean = false
        ) =>
            withResponseNotifications(
                await dispatch(
                    doUpdateIrelandH7ImportDeclaration(customerId, declarationId, declaration, calculateGrossMass)
                ),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const updateIrelandExportDeclaration = useCallback(
        async (customerId: string, declarationId: string, declaration: IrelandExportDeclarationPayload) =>
            withResponseNotifications(
                await dispatch(doUpdateIrelandExportDeclaration(customerId, declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const updateCdsDeclaration = useCallback(
        async (
            customerId: string,
            declarationId: string,
            declaration: CdsDeclarationPayload,
            withNotification: boolean = true
        ) => {
            if (withNotification) {
                return withResponseNotifications(
                    await dispatch(doUpdateCdsDeclaration(customerId, declarationId, declaration)),
                    updateDeclarationNotifs(t)
                );
            } else {
                return await dispatch(doUpdateCdsDeclaration(customerId, declarationId, declaration));
            }
        },

        [dispatch, t, withResponseNotifications]
    );

    const updateGvmsRecord = useCallback(
        async (customerId: string, declarationId: string, gvmsRecord: UkGvmsRecord) =>
            withResponseNotifications(
                await dispatch(doUpdateUkGvmsRecord(customerId, declarationId, gvmsRecord)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const updatePbnRecord = useCallback(
        async (customerId: string, declarationId: string, pbnRecord: IrelandPbnRecord) =>
            withResponseNotifications(
                await dispatch(doUpdateIePbnRecord(customerId, declarationId, pbnRecord)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const submitDeclaration = useCallback(
        async (declarationId: string, emails: NotifyEmails, applySuggestedGrossMass?: boolean) =>
            withResponseNotifications(
                await dispatch(doSubmitDeclaration(declarationId, emails, applySuggestedGrossMass)),
                submitDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const listDeclarationDocuments = useCallback(
        (declarationId: string | undefined) =>
            dispatch(
                doGetDeclarationDocuments({
                    ...defaultPagination,
                    size: 1000,
                    declarationId,
                })
            ),
        [dispatch]
    );

    const toggleCoreTemplateStatus = useCallback(
        async (declarationId: string, options: { notificationType: 'setAsCoreTemplate' | 'removeAsCoreTemplate' }) => {
            const notification =
                options.notificationType === 'setAsCoreTemplate'
                    ? setDeclarationAsCoreTemplateNotifs
                    : removeDeclarationAsCoreTemplateNotifs;

            return withResponseNotifications(
                await dispatch(doToggleCoreTemplateStatus(declarationId)),
                notification(t)
            );
        },
        [dispatch, withResponseNotifications, t]
    );

    const updateEntrySummaryDeclaration = useCallback(
        async (customerId: any, declarationId: string, declaration: IrelandEntrySummaryDeclarationPayload) =>
            withResponseNotifications(
                await dispatch(doUpdateEntrySummaryDeclaration(declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const createEnsIcs2Declaration = useCallback(
        async (customerId: string, ics2ENSDeclaration: ICS2EnsDeclarationRequest, jobId?: string) =>
            dispatch(
                doCreateIcs2EnsDeclaration(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.ICS2,
                    declarationInternalType: DeclarationInternalType.ENS,
                    ics2ENSDeclaration,
                })
            ),
        [dispatch]
    );

    const updateEnsIcs2Declaration = useCallback(
        async (
            customerId: string,
            declarationId: string,
            declaration: Declaration,
            withNotification: boolean = true
        ) => {
            const data: Declaration = declaration.ics2ENSDeclaration
                ? {
                      ...declaration,
                      ics2ENSDeclaration: await withFreshEnsIcs2GoodsItems(
                          declarationId,
                          toIcs2EnsDeclarationRequest(declaration.ics2ENSDeclaration)
                      ),
                  }
                : declaration;

            if (!withNotification) return await dispatch(doUpdateIcs2EnsDeclaration(declarationId, data));

            return withResponseNotifications(
                await dispatch(doUpdateIcs2EnsDeclaration(declarationId, data)),
                updateDeclarationNotifs(t)
            );
        },
        [dispatch, withResponseNotifications, t]
    );

    /**
     * Normalises the payload exactly as the update path does, so the backend receives the shape it
     * stored - it compares the two to reject changes to non-amendable fields.
     */
    const amendEnsIcs2Declaration = useCallback(
        async (declarationId: string, declaration: Declaration) => {
            if (!declaration.ics2ENSDeclaration) return dispatch(doAmendIcs2EnsDeclaration(declarationId, declaration));

            const request = await withFreshEnsIcs2GoodsItems(
                declarationId,
                toIcs2EnsDeclarationRequest(declaration.ics2ENSDeclaration)
            );

            // An F11 filing is amended by an A11, an F50 by an A50, and the backend picks the service
            // to handle it by this value.
            const messageType = toIcs2AmendmentMessageType(request.messageType);
            if (!messageType) throw new Error(`No ICS2 amendment message type for ${request.messageType}.`);
            if (!declaration.mrn) throw new Error('An ICS2 amendment must reference the MRN of the filing.');

            const { lrn, ...payload } = request.payload;

            return dispatch(
                doAmendIcs2EnsDeclaration(declarationId, {
                    ...declaration,
                    ics2ENSDeclaration: {
                        messageType,
                        payload: {
                            ...payload,
                            mrn: declaration.mrn,
                            documentIssueDate: { dateTime: formatDateTimeToISO8601UTCTimestamp(moment()) },
                        },
                    },
                } as unknown as Declaration)
            );
        },
        [dispatch]
    );

    /**
     * ICS2 has no endpoint of its own for house consignments — they exist only as entries of
     * `consignmentMasterLevel.consignmentHouseLevel`, which the backend rebuilds from scratch on
     * every declaration update: an entry without an `id` is created, one with an `id` is updated and
     * one that is missing is deleted. So every one of these operations is the same declaration
     * update, and the whole house list has to be passed in each time.
     *
     * Returns the updated declaration.
     */
    const saveEnsIcs2HouseConsignments = useCallback(
        async (
            declarationId: string,
            declaration: Declaration,
            houseConsignments: ICS2ConsignmentHouseLevel[]
        ): Promise<Declaration> => {
            const request = toIcs2EnsDeclarationRequest(declaration.ics2ENSDeclaration);

            const ics2ENSDeclaration = await withFreshEnsIcs2GoodsItems(declarationId, {
                ...request,
                payload: {
                    ...request.payload,
                    consignmentMasterLevel: {
                        ...(request.payload.consignmentMasterLevel ?? {}),
                        consignmentHouseLevel: houseConsignments,
                    },
                },
            });

            return dispatch(doUpdateIcs2EnsDeclaration(declarationId, { ics2ENSDeclaration }));
        },
        [dispatch]
    );

    /**
     * The new entry is appended, so the last house consignment of the returned declaration is the
     * one that was just created — and it carries the id needed to add house level goods items
     * through `createEnsIcs2Product`.
     */
    const createEnsIcs2HouseConsignment = useCallback(
        async (declarationId: string, declaration: Declaration, houseConsignment: ICS2ConsignmentHouseLevel = {}) =>
            saveEnsIcs2HouseConsignments(declarationId, declaration, [
                ...getEnsIcs2HouseConsignments(declaration),
                houseConsignment,
            ]),
        [saveEnsIcs2HouseConsignments]
    );

    /** Deletes the house consignment, and with it the goods items it holds. */
    const deleteEnsIcs2HouseConsignment = useCallback(
        async (declarationId: string, declaration: Declaration, houseConsignmentId: string) =>
            saveEnsIcs2HouseConsignments(
                declarationId,
                declaration,
                getEnsIcs2HouseConsignments(declaration).filter(({ id }) => id !== houseConsignmentId)
            ),
        [saveEnsIcs2HouseConsignments]
    );

    const duplicateDeclaration = useCallback(
        (declarationId: string, customerId: string, jobId: string) =>
            dispatch(doDuplicateDeclaration(declarationId, customerId, jobId)),
        [dispatch]
    );

    const invalidateDeclaration = useCallback(
        (declarationId: string, reason: string, declarationType: DeclarationInternalType) => {
            if (declarationType === DeclarationInternalType.IMPORT) {
                return dispatch(doInvalidateDeclaration(declarationId, reason));
            } else if (declarationType === DeclarationInternalType.EXPORT) {
                return dispatch(doInvalidateExportDeclaration(declarationId, reason));
            }
        },
        [dispatch]
    );

    const invalidateNctsDeclaration = useCallback(
        (declarationId: string, decision: boolean, justification: string, initiatedByCustoms: boolean) => {
            return dispatch(doInvalidateNctsDeclaration(declarationId, decision, justification, initiatedByCustoms));
        },
        [dispatch]
    );

    const invalidateUkNctsDeclaration = useCallback(
        (declarationId: string, decision: boolean, justification: string, initiatedByCustoms: boolean) => {
            return dispatch(doInvalidateUkNctsDeclaration(declarationId, decision, justification, initiatedByCustoms));
        },
        [dispatch]
    );

    const importPresentationNotification = useCallback(
        async (declarationId: string, type?: string, body?: {}) => {
            return dispatch(doSubmitImportPresentationNotification(declarationId, type, body));
        },
        [dispatch]
    );

    const submitIrelandImportDocuments = useCallback(
        async (declarationId: string, documentIds: string[]) => {
            return dispatch(doSubmitIrelandImportDocuments(declarationId, documentIds));
        },
        [dispatch]
    );

    const createAmendmentIrelandImportDeclaration = useCallback(
        (reason: string, declarationId: string) => {
            return dispatch(doCreateAmendmentIrelandImportDeclaration(declarationId, reason));
        },
        [dispatch]
    );

    const createAmendmentIrelandExportDeclaration = useCallback(
        (declarationId: string) => {
            return dispatch(doCreateAmendmentIrelandExportDeclaration(declarationId));
        },
        [dispatch]
    );

    const createAmendmentGmrRecord = useCallback(
        (declarationId: string) => {
            return dispatch(doCreateAmendmentGmrRecord(declarationId));
        },
        [dispatch]
    );

    const cancelAmendmentIrelandExportDeclaration = useCallback(
        async (declarationId: string) =>
            withResponseNotifications(
                await dispatch(doCancelAmendmentIrelandExportDeclaration(declarationId)),
                cancelAmendmentNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const cancelAmendmentGmrRecord = useCallback(
        async (declarationId: string) =>
            withResponseNotifications(
                await dispatch(doCancelAmendmentGmrRecord(declarationId)),
                cancelAmendmentNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const createAmendmentIrelandEntrySummaryDeclaration = useCallback(
        (reason: string, declarationId: string) => {
            return dispatch(doCreateAmendmentIrelandEntrySummaryDeclaration(declarationId, reason));
        },
        [dispatch]
    );

    const getDeclarationHistory = useCallback(
        (declarationId: string) => {
            return dispatch(doGetDeclarationHistory(declarationId));
        },
        [dispatch]
    );

    const createElectronicTransportDocument = useCallback(
        (customerId: string, ieImportElectronicTransportDocument?: ElectronicTransportDocument, jobId?: string) =>
            dispatch(
                doCreateElectronicTransportDocument(customerId, {
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                    declarationInternalType: DeclarationInternalType.ETD,
                    ieImportElectronicTransportDocument,
                })
            ),
        [dispatch]
    );

    const updateElectronicTransportDocument = useCallback(
        async (customerId: string, declarationId: string, declaration: ElectronicTransportDocumentPayload) =>
            withResponseNotifications(
                await dispatch(doUpdateElectronicTransportDocument(customerId, declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const createArrivalAtExitDeclaration = useCallback(
        (customerId: string, selectedDeclaration: { id: string; mrn: string }, jobId?: string) =>
            dispatch(
                doCreateArrivalAtExitDeclaration(customerId, {
                    id: selectedDeclaration.id,
                    jobId,
                    declarationExternalEntity: DeclarationExternalEntity.REVENUE,
                    declarationInternalType: DeclarationInternalType.ARRIVAL,
                    ieArrivalAtExitDeclaration: {
                        messageType: AESMessageTypes.B1, // not used, mandatory when submitting
                        exportOperation: { mrn: selectedDeclaration.mrn },
                    },
                })
            ),
        [dispatch]
    );

    const updateArrivalAtExitDeclaration = useCallback(
        async (customerId: string, declarationId: string, declaration: ArrivalAtExitDeclarationPayload) =>
            withResponseNotifications(
                await dispatch(doUpdateArrivalAtExitDeclaration(customerId, declarationId, declaration)),
                updateDeclarationNotifs(t)
            ),
        [dispatch, withResponseNotifications, t]
    );

    const cancelCdsDeclaration = useCallback(
        (reason: string) => dispatch(doCancelCdsDeclaration(declaration?.id, reason)),
        [declaration?.id, dispatch]
    );

    const cancelAmendment = (declaration: Declaration) => {
        if (!declaration?.id) return;

        if (declaration?.ieExportDeclaration) {
            cancelAmendmentIrelandExportDeclaration(declaration.id);
        } else if (declaration?.gvmsDeclaration) {
            cancelAmendmentGmrRecord(declaration.id);
        }
    };

    const createAmendment = (declaration: Declaration, declarationId: string): Promise<any> | undefined => {
        if (declaration?.ieExportDeclaration) {
            return createAmendmentIrelandExportDeclaration(declarationId);
        } else if (declaration?.gvmsDeclaration) {
            return createAmendmentGmrRecord(declarationId);
        }
    };

    return {
        isLoading,
        error,
        declaration,
        declarationTemplate,
        documents,
        getDeclaration,
        getDeclarationTemplate,
        createIrelandImportDeclaration,
        createIrelandH7ImportDeclaration,
        createIrelandExportDeclaration,
        createUkExportDeclaration,
        createGvmsRecord,
        createPbnRecord,
        updateGvmsRecord,
        updatePbnRecord,
        createUkImportNewDeclaration,
        updateIrelandImportDeclaration,
        updateIrelandExportDeclaration,
        updateIrelandH7ImportDeclaration,
        updateCdsDeclaration,
        submitDeclaration,
        listDeclarationDocuments,
        toggleCoreTemplateStatus,
        updateEntrySummaryDeclaration,
        createEntrySummaryDeclaration,
        createTemporaryStorageDeclaration,
        updateTemporaryStorageDeclaration,
        duplicateDeclaration,
        invalidateDeclaration,
        invalidateNctsDeclaration,
        invalidateUkNctsDeclaration,
        importPresentationNotification,
        submitIrelandImportDocuments,
        createAmendmentIrelandImportDeclaration,
        createAmendment,
        declarationHistory,
        createElectronicTransportDocument,
        updateElectronicTransportDocument,
        createArrivalAtExitDeclaration,
        updateArrivalAtExitDeclaration,
        createAmendmentIrelandEntrySummaryDeclaration,
        createIrelandH1ImportDeclarationWithFile,
        createIrelandH7ImportDeclarationsWithBulkUpload,
        cancelCdsDeclaration,
        cancelAmendment,
        getDeclarationHistory,
        createNctsDeclaration,
        createUkNctsDeclaration,
        updateNctsDeclaration,
        updateUkNctsDeclaration,
        createEnsIcs2Declaration,
        updateEnsIcs2Declaration,
        amendEnsIcs2Declaration,
        createEnsIcs2HouseConsignment,
        deleteEnsIcs2HouseConsignment,
    };
};

export default useDeclarations;
